// AssessmentCards — Three-axis assessment scores display
import { AlertTriangle } from 'lucide-react'
import ConfidenceGate from '../shared/ConfidenceGate'

interface AssessmentCardsProps {
  scores?: {
    ip_novelty: number
    market_feasibility: number
    technical_feasibility: number
  }
  /**
   * Set when the engine ran and recorded `recommendation: 'insufficient_basis'`
   * (Story #6172 F11) — mirrors AssessmentPanel.tsx's same prop so the
   * Overview and Orchestrator tabs never disagree about a project's
   * assessment state (Demo UX findings, 22 Aug 2026).
   */
  insufficientBasisReason?: string
}

function ScoreCard({ label, score, color }: { label: string; score: number; color: string }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{score}<span className="text-sm text-gray-500">/100</span></div>
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  )
}

export default function AssessmentCards({ scores, insufficientBasisReason }: AssessmentCardsProps) {
  if (insufficientBasisReason) {
    return (
      <ConfidenceGate
        icon={<AlertTriangle />}
        title="Not enough to assess yet"
        description={insufficientBasisReason}
        compact
      />
    )
  }

  if (!scores) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {['Invention Novelty', 'Market Feasibility', 'Technical Feasibility'].map((label) => (
          <div key={label} className="bg-white border rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className="text-sm text-gray-500 italic">Not assessed</div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      <ScoreCard label="Invention Novelty" score={scores.ip_novelty} color="bg-green-500" />
      <ScoreCard label="Market Feasibility" score={scores.market_feasibility} color="bg-orange-500" />
      <ScoreCard label="Technical Feasibility" score={scores.technical_feasibility} color="bg-blue-500" />
    </div>
  )
}
